/**
 * ProjectSceneForm — authoring form for `project` and `capstone` scene types.
 *
 * Renders in the GenericSceneEditorPage canvas slot when scene_type is
 * 'project' or 'capstone'. Collects brief_md, deliverables, grading_config,
 * and a rubric. The parent is responsible for saving (via sceneAPI.update).
 *
 * Props:
 *   sceneType  — 'project' | 'capstone'
 *   value      — current domain_metadata object
 *   onChange   — (newDomainMetadata) => void
 */

import React from 'react';

const DEFAULT_GRADING_CONFIG = {
  review_levels: 1,
  min_pass_score: 70,
  max_submissions: 3,
  gates_certificate: false,
  gates_module: false,
  notify_on_ai_grade: true,
  notify_on_coach_review: false,
};

export default function ProjectSceneForm({ sceneType, value, onChange }) {
  const data = value || {};

  // Build grading config with type-appropriate defaults. For capstone, override
  // the generic defaults so that gates_certificate and review_levels default to
  // the more restrictive values — but never override what the coach already saved.
  const capstonePatch = sceneType === 'capstone'
    ? { gates_certificate: true, review_levels: 2 }
    : {};
  const gradingConfig = {
    ...DEFAULT_GRADING_CONFIG,
    ...capstonePatch,
    ...(data.grading_config || {}),
  };

  const update = (field, val) => onChange({ ...data, [field]: val });
  const updateGrading = (field, val) =>
    onChange({ ...data, grading_config: { ...gradingConfig, [field]: val } });

  const sectionStyle = {
    background: '#1a1d2e',
    border: '1px solid #2d3748',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  };
  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 6,
  };
  const inputStyle = {
    width: '100%',
    background: '#0f1117',
    border: '1px solid #334155',
    borderRadius: 4,
    color: '#e2e8f0',
    padding: '6px 8px',
    fontSize: 13,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
  };
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  };
  const hintStyle = { fontSize: 11, color: '#64748b', marginTop: 3 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Brief / Instructions */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Project Brief / Instructions</label>
        <textarea
          rows={7}
          value={data.brief_md || ''}
          onChange={(e) => update('brief_md', e.target.value)}
          placeholder="Describe the project scenario, context, and what the learner needs to do…"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <div style={hintStyle}>Supports Markdown</div>
      </div>

      {/* Deliverables */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Deliverables</label>
        <DeliverablesList
          deliverables={data.deliverables || []}
          onChange={(v) => update('deliverables', v)}
          inputStyle={inputStyle}
        />
      </div>

      {/* Grading Configuration */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#93c5fd', marginBottom: 12 }}>
          Grading Configuration
        </div>
        <div style={gridStyle}>
          <div>
            <label style={labelStyle}>Review Levels</label>
            <select
              value={gradingConfig.review_levels}
              onChange={(e) => updateGrading('review_levels', parseInt(e.target.value, 10))}
              style={inputStyle}
            >
              <option value={1}>1 — AI grades, learner notified immediately</option>
              <option value={2}>2 — AI grades, then coach approves</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Min Pass Score (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={gradingConfig.min_pass_score}
              onChange={(e) => updateGrading('min_pass_score', parseInt(e.target.value, 10))}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Max Submissions</label>
            <input
              type="number"
              min={1}
              max={100}
              value={gradingConfig.max_submissions}
              onChange={(e) => updateGrading('max_submissions', parseInt(e.target.value, 10))}
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <CheckboxRow
              id="gc_gates_cert"
              label="Gates Certificate"
              checked={Boolean(gradingConfig.gates_certificate)}
              onChange={(v) => updateGrading('gates_certificate', v)}
            />
            <CheckboxRow
              id="gc_gates_mod"
              label="Gates Module Progression"
              checked={Boolean(gradingConfig.gates_module)}
              onChange={(v) => updateGrading('gates_module', v)}
            />
            <CheckboxRow
              id="gc_notify_ai"
              label="Notify on AI Grade"
              checked={Boolean(gradingConfig.notify_on_ai_grade)}
              onChange={(v) => updateGrading('notify_on_ai_grade', v)}
            />
            <CheckboxRow
              id="gc_notify_coach"
              label="Notify on Coach Review"
              checked={Boolean(gradingConfig.notify_on_coach_review)}
              onChange={(v) => updateGrading('notify_on_coach_review', v)}
            />
          </div>
        </div>

        {sceneType === 'capstone' && (
          <div style={{ marginTop: 10, padding: '6px 10px', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 5, fontSize: 12, color: '#c084fc' }}>
            Capstone default: review_levels=2, gates_certificate=true. Learners cannot receive their domain certificate until this project passes.
          </div>
        )}
      </div>

      {/* Grading Rubric */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#86efac', marginBottom: 12 }}>
          Grading Rubric
        </div>
        <RubricEditor
          criteria={data.rubric?.criteria || []}
          onChange={(criteria) => update('rubric', { ...(data.rubric || {}), criteria })}
          inputStyle={inputStyle}
        />
      </div>
    </div>
  );
}

function CheckboxRow({ id, label, checked, onChange }) {
  return (
    <label
      htmlFor={id}
      style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, color: '#94a3b8' }}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: '#6366f1', cursor: 'pointer' }}
      />
      {label}
    </label>
  );
}

function DeliverablesList({ deliverables, onChange, inputStyle }) {
  const add = () =>
    onChange([...deliverables, { type: 'text', label: 'Written analysis' }]);
  const remove = (i) => onChange(deliverables.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const updated = [...deliverables];
    updated[i] = { ...updated[i], [field]: val };
    onChange(updated);
  };

  return (
    <div>
      {deliverables.map((d, i) => (
        <div
          key={i}
          style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}
        >
          <select
            value={d.type}
            onChange={(e) => update(i, 'type', e.target.value)}
            style={{ ...inputStyle, width: 110, flexShrink: 0 }}
          >
            <option value="text">Text</option>
            <option value="file">File Upload</option>
            <option value="url">URL</option>
            <option value="code">Code</option>
          </select>
          <input
            value={d.label}
            onChange={(e) => update(i, 'label', e.target.value)}
            placeholder="Deliverable description"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            style={{
              background: '#7f1d1d',
              color: '#fca5a5',
              border: '1px solid #b91c1c',
              borderRadius: 4,
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        style={{
          background: 'rgba(99,102,241,0.15)',
          color: '#a5b4fc',
          border: '1px solid rgba(99,102,241,0.4)',
          borderRadius: 4,
          padding: '5px 12px',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        + Add Deliverable
      </button>
    </div>
  );
}

function RubricEditor({ criteria, onChange, inputStyle }) {
  const add = () =>
    onChange([
      ...criteria,
      { id: `c${Date.now()}`, name: '', description: '', max_score: 20, weight: 1 },
    ]);
  const remove = (i) => onChange(criteria.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const updated = [...criteria];
    updated[i] = { ...updated[i], [field]: val };
    onChange(updated);
  };

  const totalMax = criteria.reduce((acc, c) => acc + (Number(c.max_score) || 0), 0);

  return (
    <div>
      {criteria.map((c, i) => (
        <div
          key={c.id || i}
          style={{
            border: '1px solid #334155',
            borderRadius: 6,
            padding: 12,
            marginBottom: 8,
            background: '#0f1117',
          }}
        >
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input
              value={c.name}
              onChange={(e) => update(i, 'name', e.target.value)}
              placeholder="Criterion name (e.g. Problem Statement)"
              style={{ ...inputStyle, flex: 2 }}
            />
            <input
              type="number"
              value={c.max_score}
              onChange={(e) => update(i, 'max_score', parseInt(e.target.value, 10) || 0)}
              placeholder="Max pts"
              title="Max score for this criterion"
              style={{ ...inputStyle, width: 72, flexShrink: 0 }}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              style={{
                background: '#7f1d1d',
                color: '#fca5a5',
                border: '1px solid #b91c1c',
                borderRadius: 4,
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
          <textarea
            value={c.description}
            onChange={(e) => update(i, 'description', e.target.value)}
            placeholder="What does a strong submission look like for this criterion?"
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
        <button
          type="button"
          onClick={add}
          style={{
            background: 'rgba(34,197,94,0.12)',
            color: '#86efac',
            border: '1px solid rgba(34,197,94,0.35)',
            borderRadius: 4,
            padding: '5px 12px',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          + Add Criterion
        </button>
        {criteria.length > 0 && (
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Total: {totalMax} pts across {criteria.length} criterion{criteria.length !== 1 ? 'a' : ''}
          </span>
        )}
      </div>
    </div>
  );
}
